import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@Components/ui-v2/dialog';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { FEEDBACK_NEGATIVE_REASON_CHIPS, FEEDBACK_POSITIVE_REASON_CHIPS, FeedbackReason } from '@Models';

export type FeedbackModalMode = 'negative' | 'positive';

interface FeedbackModalProps {
  open: boolean;
  mode: FeedbackModalMode;
  /** Radix contract: called with false on Esc, backdrop, X and Cancel. */
  onOpenChange: (open: boolean) => void;
  onSubmit: (reason: FeedbackReason | null, comment: string) => void;
}

const COMMENT_MAX_LENGTH = 2000;

const MODE_COPY = {
  negative: {
    chips: FEEDBACK_NEGATIVE_REASON_CHIPS,
    title: 'Agent.FeedbackTitle',
    prompt: 'Agent.FeedbackWhatWentWrong',
    placeholder: 'Agent.FeedbackExpectedPlaceholder',
  },
  positive: {
    chips: FEEDBACK_POSITIVE_REASON_CHIPS,
    title: 'Agent.FeedbackPositiveTitle',
    prompt: 'Agent.FeedbackWhatWorkedWell',
    placeholder: 'Agent.FeedbackLikedPlaceholder',
  },
} as const;

// `w-[calc(100vw-2rem)]` keeps a gutter on the 380px sidebar shell.
const CONTENT_CLASS =
  'w-[calc(100vw-2rem)] max-w-[520px] rounded-xl border-gray-200 bg-[#fbfaf9] p-6 sm:rounded-xl ' +
  'ease-[cubic-bezier(0.16,1,0.3,1)] data-[state=open]:duration-[240ms] data-[state=closed]:duration-[200ms] ' +
  'motion-reduce:duration-0 motion-reduce:animate-none';

const FeedbackModal = ({ open, mode, onOpenChange, onSubmit }: FeedbackModalProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState<FeedbackReason | null>(null);
  const [comment, setComment] = useState('');
  const copy = MODE_COPY[mode];

  // Layout effect: the component stays mounted, so a passive one would paint
  // the previous vote for a frame while the dialog animates in.
  useLayoutEffect(() => {
    if (!open) return;
    setReason(null);
    setComment('');
  }, [open, mode]);

  const trimmedComment = comment.trim();

  const handleSubmit = () => onSubmit(reason, trimmedComment);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={CONTENT_CLASS}>
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-gray-900">{t(copy.title)}</DialogTitle>
        </DialogHeader>

        <div>
          <p id="feedback-reasons" className="mb-2 text-sm text-gray-600">
            {t(copy.prompt)}
          </p>
          <div role="group" aria-labelledby="feedback-reasons" className="flex flex-wrap gap-1.5">
            {copy.chips.map(({ reason: chipReason, labelKey }) => {
              const isSelected = reason === chipReason;
              return (
                <button
                  key={chipReason}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setReason(isSelected ? null : chipReason)}
                  className={`cursor-pointer rounded-full border px-3 py-1 text-xs transition-colors duration-200 ${
                    isSelected
                      ? 'border-[#7c6cf6] bg-[#7c6cf6]/10 text-[#7c6cf6]'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-[#7c6cf6]/50'
                  }`}>
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        <textarea
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          aria-label={t(copy.placeholder)}
          placeholder={t(copy.placeholder)}
          rows={3}
          maxLength={COMMENT_MAX_LENGTH}
          className="box-border w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:border-[#7c6cf6] focus:outline-none focus:ring-1 focus:ring-[#7c6cf6]/30"
        />

        <DialogDescription className="text-[11px] leading-snug text-gray-400">
          {t('Agent.FeedbackDisclosure')}
        </DialogDescription>

        <DialogFooter className="gap-2 sm:space-x-0">
          <TrackedButton
            buttonName="FEEDBACK_CANCELED"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer">
            {t('Agent.FeedbackCancel')}
          </TrackedButton>
          <TrackedButton
            buttonName="FEEDBACK_SUBMITTED"
            extraLogData={{ mode, reason: reason ?? 'none', hasComment: trimmedComment.length > 0 }}
            size="sm"
            onClick={handleSubmit}
            className="cursor-pointer bg-[#7c6cf6] text-white hover:bg-[#6b5ce5]">
            {t('Agent.FeedbackSubmit')}
          </TrackedButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
